import { toast } from "react-hot-toast";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { BookOpenIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { fetchOfferSettings, updateOfferSettings } from "../../../api/offer-settings";

const GROUP_ORDER = ["workflow", "calculation", "document", "import", "overview"];

const SettingsCard = ({ id, title, description, children }) => (
  <section
    id={id}
    className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
  >
    <div className="mb-4">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

const ToggleField = ({ id, label, description, checked, recommended, disabled, saving, onChange }) => (
  <label
    id={id}
    className="flex scroll-mt-28 items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3"
  >
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled || saving}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400 disabled:cursor-not-allowed"
    />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        {recommended ? (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
            {recommended}
          </span>
        ) : null}
      </div>
      {description ? <div className="mt-0.5 text-sm text-slate-600">{description}</div> : null}
    </div>
  </label>
);

export const OffersSettings = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [catalog, setCatalog] = useState({ flags: {}, groups: {}, recommended: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const loadSettings = () => {
    setIsLoading(true);
    fetchOfferSettings()
      .then((res) => {
        const data = res?.data?.data !== undefined ? res.data.data : res?.data || {};
        setCatalog({
          flags: data.flags && typeof data.flags === "object" ? data.flags : {},
          groups: data.groups && typeof data.groups === "object" ? data.groups : {},
          recommended: Array.isArray(data.recommended) ? data.recommended : [],
        });
      })
      .catch(() => {
        toast.error(t("offers.settings.load_error"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash, isLoading]);

  const recommendedSet = useMemo(
    () => new Set(catalog.recommended || []),
    [catalog.recommended]
  );

  const groupedKeys = useMemo(() => {
    const groups = catalog.groups || {};
    const seen = new Set();
    const result = GROUP_ORDER.filter((g) => Array.isArray(groups[g])).map((g) => {
      (groups[g] || []).forEach((k) => seen.add(k));
      return { id: g, keys: groups[g] };
    });
    const leftover = Object.keys(catalog.flags || {}).filter((k) => !seen.has(k));
    if (leftover.length) {
      result.push({ id: "other", keys: leftover });
    }
    return result;
  }, [catalog.flags, catalog.groups]);

  const handleToggle = async (key, value) => {
    const previous = catalog.flags?.[key];
    setCatalog((prev) => ({
      ...prev,
      flags: { ...prev.flags, [key]: value },
    }));
    setSavingKey(key);
    try {
      const res = await updateOfferSettings({ [key]: value });
      const data = res?.data || res;
      if (data?.flags) {
        setCatalog((prev) => ({
          ...prev,
          flags: data.flags,
          groups: data.groups || prev.groups,
          recommended: data.recommended || prev.recommended,
        }));
      }
      toast.success(t("offers.settings.saved"));
    } catch {
      setCatalog((prev) => ({
        ...prev,
        flags: { ...prev.flags, [key]: previous },
      }));
      toast.error(t("offers.settings.save_error"));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <DashboardHeader
      subtitle={t("offers.settings.subtitle")}
      actions={[]}
      title={t("offers.settings.title")}
      meta={[]}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-4xl">
        {isLoading ? (
          <ResourceLoading />
        ) : (
          <>
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <p className="text-sm leading-relaxed text-slate-600">{t("offers.settings.intro")}</p>
              <Link
                to="/dashboard/guide/offers#offers-setup-status"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
              >
                <BookOpenIcon className="h-4 w-4" />
                {t("offers.settings.open_guide")}
              </Link>
            </section>

            {groupedKeys.map((group) => (
              <SettingsCard
                key={group.id}
                id={`group-${group.id}`}
                title={t(`offers.settings.groups.${group.id}`, {
                  defaultValue: group.id,
                })}
                description={t(`offers.settings.groups.${group.id}_hint`, {
                  defaultValue: "",
                })}
              >
                {(group.keys || []).map((key) => (
                  <ToggleField
                    key={key}
                    id={`flag-${key}`}
                    label={t(`offers.settings.flags.${key}.title`, { defaultValue: key })}
                    description={t(`offers.settings.flags.${key}.description`, {
                      defaultValue: "",
                    })}
                    checked={!!catalog.flags?.[key]}
                    recommended={
                      recommendedSet.has(key) ? t("offers.settings.recommended") : null
                    }
                    saving={savingKey === key}
                    onChange={(value) => handleToggle(key, value)}
                  />
                ))}
              </SettingsCard>
            ))}
          </>
        )}
      </div>
    </DashboardHeader>
  );
};
